import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    HiOutlineArrowsRightLeft,
    HiOutlineCalendar,
    HiOutlineUser,
} from "react-icons/hi2";
import { HiOutlineDotsHorizontal } from "react-icons/hi";
import DatePicker from "./DatePicker";
import PassengersDropdown from "./PassengersDropdown";
import { formatPassengerDisplay } from "../../utils/passengerUtils";
import { encodeSearchParams } from "../../utils/searchUtils";

const BookingWidget: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [tripType, setTripType] = useState("Return trip");
    const [payWithPoints, setPayWithPoints] = useState(false);
    const [fromValue, setFromValue] = useState("");
    const [toValue, setToValue] = useState("");
    const [fromFocused, setFromFocused] = useState(false);
    const [toFocused, setToFocused] = useState(false);
    const [showFromDropdown, setShowFromDropdown] = useState(false);
    const [showToDropdown, setShowToDropdown] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [departureDate, setDepartureDate] = useState("");
    const [returnDate, setReturnDate] = useState("");
    const [showPassengersDropdown, setShowPassengersDropdown] = useState(false);
    const [adults, setAdults] = useState(1);
    const [children, setChildren] = useState(0);
    const [infants, setInfants] = useState(0);
    const passengersRef = useRef<HTMLDivElement>(null);

    const tabs = ["Flights", "SAS Young", "SAS for Work"];

    const airports = [
        {
            code: "OSL",
            city: "Oslo",
            country: "Norway",
            label: "Oslo OSL",
            fullLabel: "Oslo • All airports OSL",
            image: "https://www.flysas.com/_next/image?url=https%3A%2F%2Fcomponents.test.flysas.com%2Fcontent%2Fassets%2Fimages%2Fdestination%2Fosl.jpg%3Fw%3D60%26h%3D60&w=128&q=52",
        },
        {
            code: "NYC",
            city: "New York",
            country: "United States of America",
            label: "New York NYC",
            fullLabel: "New York • All airports NYC",
            image: "https://www.flysas.com/_next/image?url=https%3A%2F%2Fcomponents.test.flysas.com%2Fcontent%2Fassets%2Fimages%2Fdestination%2Fewr.jpg%3Fw%3D60%26h%3D60&w=128&q=52",
        },
    ];

    useEffect(() => {
        const origin = searchParams.get("origin");
        const destination = searchParams.get("destination");
        const outbound = searchParams.get("outboundDate");
        const inbound = searchParams.get("inboundDate");

        if (origin) {
            const airport = airports.find((a) => a.code === origin);
            if (airport) setFromValue(airport.label);
        }
        if (destination) {
            const airport = airports.find((a) => a.code === destination);
            if (airport) setToValue(airport.label);
        }
        if (outbound) {
            setDepartureDate(outbound);
        }
        if (inbound) {
            setReturnDate(inbound);
        }
    }, []);

    const handleSwap = () => {
        const tempValue = fromValue;
        setFromValue(toValue);
        setToValue(tempValue);

        const newParams = new URLSearchParams(searchParams);
        const tempOrigin = searchParams.get("origin");
        const tempDestination = searchParams.get("destination");

        if (tempDestination) {
            newParams.set("origin", tempDestination);
        } else {
            newParams.delete("origin");
        }

        if (tempOrigin) {
            newParams.set("destination", tempOrigin);
        } else {
            newParams.delete("destination");
        }

        setSearchParams(newParams);
    };

    const handleDatesChange = (departure: string, returnDateValue: string) => {
        setDepartureDate(departure);
        setReturnDate(returnDateValue);

        const newParams = new URLSearchParams(searchParams);
        if (departure) {
            newParams.set("outboundDate", departure);
        } else {
            newParams.delete("outboundDate");
        }
        if (returnDateValue) {
            newParams.set("inboundDate", returnDateValue);
        } else {
            newParams.delete("inboundDate");
        }
        setSearchParams(newParams);
    };

    const handleSearch = () => {
        // Get origin and destination codes
        const originCode = searchParams.get("origin") || "OSL";
        const destinationCode = searchParams.get("destination") || "NYC";

        // Validate required fields
        if (
            !fromValue ||
            !toValue ||
            !departureDate ||
            (tripType === "Return trip" && !returnDate)
        ) {
            alert("Please fill in all required fields");
            return;
        }

        // Create search params
        const searchData = {
            tripType:
                tripType === "Return trip"
                    ? ("RT" as const)
                    : tripType === "One way"
                    ? ("OW" as const)
                    : ("MC" as const),
            origin: originCode,
            destination: destinationCode,
            departureDate: departureDate,
            returnDate: tripType === "Return trip" ? returnDate : undefined,
            adults,
            children,
            infants,
            youths: 0,
        };

        // Encode search params
        const searchString = encodeSearchParams(searchData);

        // Navigate to search results
        navigate(`/book/flights?search=${searchString}`);
    };

    const formatDateDisplay = () => {
        const formatDate = (dateStr: string, includeYear: boolean = false) => {
            if (!dateStr) return "";
            // Parse YYYY-MM-DD format in local timezone
            const [year, month, day] = dateStr.split("-").map(Number);
            const date = new Date(year, month - 1, day);

            const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
            const months = [
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Oct",
                "Nov",
                "Dec",
            ];
            const baseFormat = `${days[date.getDay()]}, ${
                months[date.getMonth()]
            } ${date.getDate()}`;

            return includeYear ? `${baseFormat}, ${year}` : baseFormat;
        };

        if (tripType === "One way") {
            return departureDate ? formatDate(departureDate) : "Outbound";
        }
        if (departureDate && returnDate) {
            // Check if years are different
            const depYear = parseInt(departureDate.split("-")[0]);
            const retYear = parseInt(returnDate.split("-")[0]);
            const differentYears = depYear !== retYear;

            return `${formatDate(departureDate, differentYears)} – ${formatDate(
                returnDate,
                differentYears
            )}`;
        }
        if (departureDate) {
            return `${formatDate(departureDate)} – Return`;
        }
        return "Outbound – Return";
    };

    return (
        <div className="w-full -mt-[220px] relative z-20 px-28">
            <div className="bg-white rounded-2xl shadow-xl p-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-[22px] font-semibold text-sas-text">
                        Book your next trip
                    </h2>

                    {/* Tabs */}
                    <div className="relative">
                        <div className="flex items-center gap-0">
                            {tabs.map((tab) => (
                                <button
                                    key={tab}
                                    disabled={tab !== "Flights"}
                                    className={`pb-2 px-4 py-1 font-normal transition-all relative text-sas-text hover:bg-gray-100 hover:rounded-md cursor-default`}
                                >
                                    {tab}
                                    {tab === "Flights" && (
                                        <div className="absolute bottom-0 left-0 right-0 h-[4px] bg-[#00175c]"></div>
                                    )}
                                </button>
                            ))}
                            <button className="flex items-center gap-2 text-sas-text hover:bg-gray-100 hover:rounded-md px-3 py-1 transition-all">
                                {React.createElement(
                                    HiOutlineDotsHorizontal as any,
                                    { className: "text-xl" }
                                )}
                                <span>More options</span>
                            </button>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gray-500"></div>
                    </div>
                </div>

                {/* Trip Type and Points */}
                <div className="flex items-center gap-8 mb-2.5">
                    <div className="relative">
                        <select
                            value={tripType}
                            onChange={(e) => {
                                const newTripType = e.target.value;
                                setTripType(newTripType);

                                // Remove inboundDate when switching to One way
                                if (newTripType === "One way") {
                                    setReturnDate("");
                                    const newParams = new URLSearchParams(
                                        searchParams
                                    );
                                    newParams.delete("inboundDate");
                                    setSearchParams(newParams);
                                }
                            }}
                            className="appearance-none bg-white border-none rounded-lg px-2 py-1 pr-10 text-sas-text font-normal focus:outline-none focus:ring-2 focus:ring-sas-blue focus:border-transparent text-sm"
                        >
                            <option>Return trip</option>
                            <option>One way</option>
                            <option>Multi journey</option>
                        </select>
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                            <svg
                                className="w-5 h-5 text-sas-text"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                {/* <path d="M7 10l5 5 5-5z" /> */}
                            </svg>
                        </div>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer px-2 py-2 rounded-lg focus-within:ring-2 focus-within:ring-sas-blue">
                        <input
                            type="checkbox"
                            checked={payWithPoints}
                            onChange={(e) => setPayWithPoints(e.target.checked)}
                            className="w-4 h-4 border border-gray-300 rounded focus:ring-0 focus:ring-offset-0 text-black accent-black"
                        />
                        <span className="text-sas-text text-base">
                            Pay with points
                        </span>
                    </label>
                </div>

                {/* Search Fields */}
                <div className="flex items-center gap-2 mb-4">
                    {/* From Field */}
                    <div
                        className="relative"
                        style={{
                            width: tripType === "One way" ? "242px" : "225px",
                        }}
                    >
                        <label
                            className={`absolute left-4 transition-all pointer-events-none ${
                                fromFocused || fromValue
                                    ? "top-2 text-sm text-gray-500"
                                    : "top-1/2 -translate-y-1/2 text-base text-gray-500"
                            }`}
                        >
                            From
                        </label>
                        <input
                            type="text"
                            value={fromValue}
                            onChange={(e) => setFromValue(e.target.value)}
                            onFocus={() => {
                                setFromFocused(true);
                                setShowFromDropdown(true);
                            }}
                            onBlur={() => {
                                setFromFocused(false);
                                setTimeout(
                                    () => setShowFromDropdown(false),
                                    200
                                );
                            }}
                            className={`w-full h-[60px] px-4 ${
                                fromFocused || fromValue ? "pt-6 pb-2" : ""
                            } border border-[#807e7e] rounded-lg text-sas-text hover:bg-gray-50 focus:outline-none focus:border-sas-blue transition-colors`}
                        />
                        {showFromDropdown && (
                            <div
                                className="absolute top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                                style={{
                                    width: "480px",
                                    height: "380px",
                                    overflowY: "auto",
                                }}
                            >
                                {airports.map((airport) => (
                                    <button
                                        key={airport.code}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            setFromValue(airport.label);
                                            setShowFromDropdown(false);
                                            const newParams =
                                                new URLSearchParams(
                                                    searchParams
                                                );
                                            newParams.set(
                                                "origin",
                                                airport.code
                                            );
                                            setSearchParams(newParams);
                                        }}
                                        className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3"
                                    >
                                        <img
                                            src={airport.image}
                                            alt={airport.city}
                                            className="w-14 h-14 rounded-xl flex-shrink-0 object-cover"
                                        />
                                        <div className="flex-1">
                                            <div className="text-sas-text font-medium text-base">
                                                {airport.fullLabel}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="text-sm text-gray-500 mt-0.5">
                                                    {airport.country}
                                                </div>
                                                <svg
                                                    aria-labelledby="title"
                                                    width="114"
                                                    height="40"
                                                    preserveAspectRatio="xMaxYMid meet"
                                                    viewBox="0 0 114 40"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-7 h-7"
                                                >
                                                    <title>
                                                        SAS operates on this
                                                        route
                                                    </title>
                                                    <path
                                                        d="M30.8065 0C37.2845 0 40.5227 1.70852 41.7012 2.277C42.4376 2.70528 42.2897 3.27376 42.2897 3.27376L41.6688 5.91588C41.6178 6.12394 41.5792 6.33487 41.5533 6.54753C41.407 6.83254 41.1127 6.83254 40.8169 6.54753L39.489 5.78339C37.8637 4.90371 35.9427 4.12879 33.3099 4.12879C29.3353 4.12879 27.2725 6.97581 28.3031 11.1031L29.018 13.7267C29.8408 16.8669 30.6393 20.0134 31.4135 23.1659L31.6908 24.3414C33.4562 31.4589 28.451 40 14.9066 40C7.39804 40 2.68708 37.2947 1.06643 36.0145C0.183701 35.3027 -0.110542 34.8744 0.0358092 34.4477L0.479484 33.0365C0.778348 32.1152 1.13267 31.0954 1.21432 31.1739C1.21432 30.6039 1.50856 30.4622 1.80281 30.7472L2.48064 31.3819C4.25688 32.9841 8.01579 35.8712 12.9917 35.8712C19.3233 35.8712 18.8812 30.8889 18.8812 30.1772C18.8812 28.1852 16.2315 18.3623 15.4951 15.8003C13.4339 7.54429 18.587 0 30.8065 0ZM102.505 0C108.983 0 112.221 1.70852 113.399 2.277C114.136 2.70528 113.988 3.27376 113.988 3.27376L113.369 5.91588C113.317 6.12394 113.279 6.33487 113.253 6.54753C113.107 6.83254 112.812 6.83254 112.518 6.54753C110.603 5.40903 108.396 4.12879 105.01 4.12879C101.035 4.12879 98.9722 6.97581 100.003 11.1031L100.484 12.8347C101.363 16.1424 103.036 22.7469 103.389 24.3429C105.156 31.4589 100.151 40 86.6063 40C79.0978 40 74.681 37.438 73.0619 36.1562C72.3256 35.5631 71.999 35.1687 71.9943 34.8051L72.6907 31.4204C72.7138 31.3203 72.7338 31.2448 72.7461 31.2001C72.9156 30.7472 73.3562 30.6054 73.6504 30.8889L74.812 31.8995C76.7299 33.5018 80.0991 35.8712 84.6914 35.8712C91.0231 35.8712 90.5809 30.8889 90.5809 30.1772C90.5809 28.1852 87.9312 18.3623 87.1948 15.8003C85.132 7.54429 90.2851 0 102.505 0ZM74.6795 0.426745C75.0338 0.426745 75.0092 0.700971 75.0615 0.810353L75.1216 0.853489L67.4652 39.0032C67.4652 39.2174 67.382 39.2698 67.2803 39.2836L53.4786 39.2882C53.038 39.2882 53.038 39.0032 53.038 38.8615L54.6356 30.7349C54.6444 30.692 54.6516 30.6489 54.6571 30.6055L54.6186 30.5053L54.5108 30.4622H43.1724L43.0322 30.4992L42.8782 30.6055L36.106 39.0032C36.106 39.1111 36.0243 39.2174 35.7963 39.2651L35.519 39.2882H30.805C30.5862 39.2882 30.5308 39.2081 30.5785 39.0479L30.6586 38.8615L62.3121 0.711755L62.4538 0.611616C62.5854 0.537005 62.7286 0.484934 62.8774 0.457557L63.1963 0.426745H74.6795ZM58.1896 11.6731L58.0432 11.8148L46.2643 26.4767V26.6199H55.2456L58.1896 11.8148L58.2558 11.7101L58.1896 11.6731Z"
                                                        fill="#000000"
                                                    ></path>
                                                </svg>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {/* Swap Button */}
                    <button
                        onClick={handleSwap}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors shadow-md"
                    >
                        {React.createElement(HiOutlineArrowsRightLeft as any, {
                            className: "text-xl text-sas-blue",
                        })}
                    </button>
                    {/* To Field */}
                    <div
                        className="relative"
                        style={{
                            width: tripType === "One way" ? "242px" : "225px",
                        }}
                    >
                        <label
                            className={`absolute left-4 transition-all pointer-events-none ${
                                toFocused || toValue
                                    ? "top-2 text-sm text-gray-500"
                                    : "top-1/2 -translate-y-1/2 text-base text-gray-500"
                            }`}
                        >
                            To
                        </label>
                        <input
                            type="text"
                            value={toValue}
                            onChange={(e) => setToValue(e.target.value)}
                            onFocus={() => {
                                setToFocused(true);
                                setShowToDropdown(true);
                            }}
                            onBlur={() => {
                                setToFocused(false);
                                setTimeout(() => setShowToDropdown(false), 200);
                            }}
                            className={`w-full h-[60px] px-4 ${
                                toFocused || toValue ? "pt-6 pb-2" : ""
                            } border border-[#807e7e] rounded-lg text-sas-text hover:bg-gray-50 focus:outline-none focus:border-sas-blue transition-colors`}
                        />
                        {showToDropdown && (
                            <div
                                className="absolute top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                                style={{
                                    width: "480px",
                                    height: "380px",
                                    overflowY: "auto",
                                }}
                            >
                                {airports.map((airport) => (
                                    <button
                                        key={airport.code}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            setToValue(airport.label);
                                            setShowToDropdown(false);
                                            const newParams =
                                                new URLSearchParams(
                                                    searchParams
                                                );
                                            newParams.set(
                                                "destination",
                                                airport.code
                                            );
                                            setSearchParams(newParams);
                                        }}
                                        className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3"
                                    >
                                        <img
                                            src={airport.image}
                                            alt={airport.city}
                                            className="w-14 h-14 rounded-xl flex-shrink-0 object-cover"
                                        />
                                        <div className="flex-1">
                                            <div className="text-sas-text font-medium text-base">
                                                {airport.fullLabel}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="text-sm text-gray-500 mt-0.5">
                                                    {airport.country}
                                                </div>
                                                <svg
                                                    aria-labelledby="title"
                                                    width="114"
                                                    height="40"
                                                    preserveAspectRatio="xMaxYMid meet"
                                                    viewBox="0 0 114 40"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-7 h-7"
                                                >
                                                    <title>
                                                        SAS operates on this
                                                        route
                                                    </title>
                                                    <path
                                                        d="M30.8065 0C37.2845 0 40.5227 1.70852 41.7012 2.277C42.4376 2.70528 42.2897 3.27376 42.2897 3.27376L41.6688 5.91588C41.6178 6.12394 41.5792 6.33487 41.5533 6.54753C41.407 6.83254 41.1127 6.83254 40.8169 6.54753L39.489 5.78339C37.8637 4.90371 35.9427 4.12879 33.3099 4.12879C29.3353 4.12879 27.2725 6.97581 28.3031 11.1031L29.018 13.7267C29.8408 16.8669 30.6393 20.0134 31.4135 23.1659L31.6908 24.3414C33.4562 31.4589 28.451 40 14.9066 40C7.39804 40 2.68708 37.2947 1.06643 36.0145C0.183701 35.3027 -0.110542 34.8744 0.0358092 34.4477L0.479484 33.0365C0.778348 32.1152 1.13267 31.0954 1.21432 31.1739C1.21432 30.6039 1.50856 30.4622 1.80281 30.7472L2.48064 31.3819C4.25688 32.9841 8.01579 35.8712 12.9917 35.8712C19.3233 35.8712 18.8812 30.8889 18.8812 30.1772C18.8812 28.1852 16.2315 18.3623 15.4951 15.8003C13.4339 7.54429 18.587 0 30.8065 0ZM102.505 0C108.983 0 112.221 1.70852 113.399 2.277C114.136 2.70528 113.988 3.27376 113.988 3.27376L113.369 5.91588C113.317 6.12394 113.279 6.33487 113.253 6.54753C113.107 6.83254 112.812 6.83254 112.518 6.54753C110.603 5.40903 108.396 4.12879 105.01 4.12879C101.035 4.12879 98.9722 6.97581 100.003 11.1031L100.484 12.8347C101.363 16.1424 103.036 22.7469 103.389 24.3429C105.156 31.4589 100.151 40 86.6063 40C79.0978 40 74.681 37.438 73.0619 36.1562C72.3256 35.5631 71.999 35.1687 71.9943 34.8051L72.6907 31.4204C72.7138 31.3203 72.7338 31.2448 72.7461 31.2001C72.9156 30.7472 73.3562 30.6054 73.6504 30.8889L74.812 31.8995C76.7299 33.5018 80.0991 35.8712 84.6914 35.8712C91.0231 35.8712 90.5809 30.8889 90.5809 30.1772C90.5809 28.1852 87.9312 18.3623 87.1948 15.8003C85.132 7.54429 90.2851 0 102.505 0ZM74.6795 0.426745C75.0338 0.426745 75.0092 0.700971 75.0615 0.810353L75.1216 0.853489L67.4652 39.0032C67.4652 39.2174 67.382 39.2698 67.2803 39.2836L53.4786 39.2882C53.038 39.2882 53.038 39.0032 53.038 38.8615L54.6356 30.7349C54.6444 30.692 54.6516 30.6489 54.6571 30.6055L54.6186 30.5053L54.5108 30.4622H43.1724L43.0322 30.4992L42.8782 30.6055L36.106 39.0032C36.106 39.1111 36.0243 39.2174 35.7963 39.2651L35.519 39.2882H30.805C30.5862 39.2882 30.5308 39.2081 30.5785 39.0479L30.6586 38.8615L62.3121 0.711755L62.4538 0.611616C62.5854 0.537005 62.7286 0.484934 62.8774 0.457557L63.1963 0.426745H74.6795ZM58.1896 11.6731L58.0432 11.8148L46.2643 26.4767V26.6199H55.2456L58.1896 11.8148L58.2558 11.7101L58.1896 11.6731Z"
                                                        fill="#000000"
                                                    ></path>
                                                </svg>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {/* Date Field */}
                    <div
                        className="relative"
                        style={{
                            width: tripType === "One way" ? "245px" : "303px",
                        }}
                    >
                        <button
                            onClick={() => setShowDatePicker(true)}
                            className="w-full h-[60px] px-4 border border-[#807e7e] rounded-lg text-left text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-sas-blue focus:border-transparent transition-colors flex items-center gap-2"
                        >
                            {React.createElement(HiOutlineCalendar as any, {
                                className: "text-xl",
                            })}
                            <span className="text-gray-500">
                                {formatDateDisplay()}
                            </span>
                        </button>
                    </div>
                    {/* Passengers Field */}
                    <div className="relative flex-1" ref={passengersRef}>
                        <button
                            onClick={() =>
                                setShowPassengersDropdown(
                                    !showPassengersDropdown
                                )
                            }
                            className="w-full h-[60px] px-4 border border-[#807e7e] rounded-lg text-gray-500 hover:bg-gray-50 focus:outline-none transition-colors flex items-center gap-2"
                        >
                            {React.createElement(HiOutlineUser as any, {
                                className: "text-xl",
                            })}
                            <span>
                                {formatPassengerDisplay(
                                    adults,
                                    children,
                                    infants
                                )}
                            </span>
                        </button>
                        <PassengersDropdown
                            isOpen={showPassengersDropdown}
                            onClose={() => setShowPassengersDropdown(false)}
                            adults={adults}
                            children={children}
                            infants={infants}
                            onPassengersChange={(
                                newAdults,
                                newChildren,
                                newInfants
                            ) => {
                                setAdults(newAdults);
                                setChildren(newChildren);
                                setInfants(newInfants);
                            }}
                            anchorRef={passengersRef}
                        />
                    </div>
                    {/* Search Button */}
                    <button
                        onClick={handleSearch}
                        className="h-[60px] px-8 bg-sas-blue text-white rounded-full font-semibold hover:bg-sas-blue-dark transition-colors"
                    >
                        Search
                    </button>
                </div>

                {/* Additional Services */}
                <div className="flex items-center gap-6 pt-4 border-t border-gray-200">
                    <button className="flex items-center gap-2 text-sas-text px-3 py-1 rounded-full hover:bg-gray-100 transition-colors">
                        <svg
                            fill="none"
                            viewBox="0 0 24 24"
                            className="w-5 h-5"
                        >
                            <path
                                fill="currentColor"
                                fillRule="evenodd"
                                d="M2.5 6.12h7.618a9.75 9.75 0 0 1 6.42 2.412l1.3 1.137q.19.02.383.046l1.784.255a4.085 4.085 0 0 1 3.453 4.717l-.192 1.15a.94.94 0 0 1-.924.783H20.5a.75.75 0 0 1-.3-.062 2.5 2.5 0 1 1 .288-1.438h1.377l.113-.68a2.586 2.586 0 0 0-2.185-2.985L18.01 11.2a8 8 0 0 0-1.13-.08H1.75v3.03a.25.25 0 0 0 .17.236l.695.232a2.501 2.501 0 1 1-.003 1.494.75.75 0 0 1-.35-.03l-.815-.273A1.75 1.75 0 0 1 .25 14.15v-3.138c0-.573.18-1.132.512-1.598l1.355-1.898A.75.75 0 0 1 2.5 6.12m-.043 3.5h13.047a8.25 8.25 0 0 0-5.386-2H3.886zM6 15.37a1 1 0 1 1-2 0 1 1 0 0 1 2 0m13 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0"
                                clipRule="evenodd"
                            ></path>
                            <path
                                fill="currentColor"
                                d="M8.491 15.12h6.018a3.491 3.491 0 0 0 .221 1.5H8.27a3.5 3.5 0 0 0 .221-1.5"
                            ></path>
                        </svg>
                        <span className="font-semibold">Rental car</span>
                    </button>
                    <button className="flex items-center gap-2 text-sas-text px-3 py-1 rounded-full hover:bg-gray-100 transition-colors">
                        <svg
                            fill="none"
                            viewBox="0 0 24 24"
                            className="w-5 h-5"
                        >
                            <path
                                fill="currentColor"
                                d="M2.365 19.673V4.788q0-.227.096-.41a1 1 0 0 1 .276-.322l3.846-2.789a.917.917 0 0 1 1.065 0l3.846 2.789q.18.14.276.322a.9.9 0 0 1 .095.41v2.289h8.866q.383 0 .644.26.26.26.26.644v11.692q0 .383-.26.644a.88.88 0 0 1-.644.26H3.269a.88.88 0 0 1-.644-.26.88.88 0 0 1-.26-.644m1.5-.596h2.5v-2.5h-2.5zm0-4h2.5v-2.5h-2.5zm0-4h2.5v-2.5h-2.5zm0-4h2.5v-2.5h-2.5zm4 0h2.5v-2.5h-2.5zm0 12h12.27v-10.5H7.865zm6.943-8H17q.318 0 .534.216a.73.73 0 0 1 .216.534q0 .32-.216.535a.73.73 0 0 1-.534.215h-2.192a.73.73 0 0 1-.535-.216.73.73 0 0 1-.215-.534q0-.32.215-.535a.73.73 0 0 1 .535-.215m0 4H17q.318 0 .534.216a.73.73 0 0 1 .216.534q0 .32-.216.535a.73.73 0 0 1-.534.215h-2.192a.73.73 0 0 1-.535-.216.73.73 0 0 1-.215-.534q0-.32.215-.535a.73.73 0 0 1 .535-.215m-2.943-3.25q0 .32-.215.535a.73.73 0 0 1-.535.215.73.73 0 0 1-.534-.216.73.73 0 0 1-.216-.534q0-.32.216-.535a.73.73 0 0 1 .535-.215q.318 0 .534.216a.73.73 0 0 1 .215.534m-.75 4.75a.73.73 0 0 1-.534-.216.73.73 0 0 1-.216-.534q0-.32.216-.535a.73.73 0 0 1 .535-.215q.318 0 .534.216a.73.73 0 0 1 .215.534q0 .32-.215.535a.73.73 0 0 1-.535.215"
                            ></path>
                        </svg>
                        <span className="font-semibold">Hotel</span>
                    </button>
                    <button className="flex items-center gap-2 text-sas-text px-3 py-1 rounded-full hover:bg-gray-100 transition-colors">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            className="w-5 h-5"
                        >
                            <path
                                fill="currentColor"
                                d="M3 22.75a.73.73 0 0 1-.534-.215A.73.73 0 0 1 2.25 22q0-.319.216-.534A.73.73 0 0 1 3 21.25q.67 0 1.321-.177a5.2 5.2 0 0 0 1.256-.527.84.84 0 0 1 .423-.105q.237 0 .423.105.604.351 1.256.527a5 5 0 0 0 2.642 0 5.2 5.2 0 0 0 1.256-.527.86.86 0 0 1 .43-.105q.245 0 .416.105a5.2 5.2 0 0 0 1.256.527 5 5 0 0 0 2.642 0 5.2 5.2 0 0 0 1.256-.527.8.8 0 0 1 .423-.105.8.8 0 0 1 .423.105 5.2 5.2 0 0 0 1.256.527A5 5 0 0 0 21 21.25q.32 0 .535.216a.73.73 0 0 1 .215.534.73.73 0 0 1-.215.535.73.73 0 0 1-.535.215q-.746 0-1.496-.192A7 7 0 0 1 18 21.97a7 7 0 0 1-1.504.587q-.75.192-1.496.192t-1.496-.192A7 7 0 0 1 12 21.97a7 7 0 0 1-1.504.587q-.75.192-1.496.192t-1.496-.192A7 7 0 0 1 6 21.97a7 7 0 0 1-1.504.587q-.75.192-1.496.192m8.752-12.77 3.998 3.145v3.85q.381.265.837.628l.788.628q-.42.354-1.03.59a3.7 3.7 0 0 1-1.345.237q-.842 0-1.622-.363A4.4 4.4 0 0 1 12 17.683a4.3 4.3 0 0 1-1.33.998 3.7 3.7 0 0 1-1.497.367q-.154 0-.3-.023a2 2 0 0 1-.284-.07q-2.286-1.484-3.812-3.07-1.527-1.587-1.527-2.61 0-.62.518-.823a3 3 0 0 1 1.082-.202q.705 0 1.81.266 1.104.265 2.457.819L8.323 9.83a1.63 1.63 0 0 1 .06-.94q.17-.468.552-.73l2.698-1.91a8 8 0 0 1-.488-.087l-1.727-.337a8 8 0 0 0-.487-.087L6.844 7.173a.78.78 0 0 1-.562.117.72.72 0 0 1-.482-.315.78.78 0 0 1-.111-.575.69.69 0 0 1 .319-.479L8.285 4.36a.9.9 0 0 1 .324-.144.8.8 0 0 1 .349.003l5.092.94q.292.051.574.242t.468.534l.875 1.55q.555.988 1.495 1.672a5.06 5.06 0 0 0 2.113.893q.304.066.509.289a.75.75 0 0 1 .204.526.68.68 0 0 1-.232.527.64.64 0 0 1-.537.168 6.44 6.44 0 0 1-2.89-1.17 7.2 7.2 0 0 1-2.089-2.386zm-1.548.899.537 3.13q.75.364 1.742.916.99.552 1.767 1.017v-1.97zM17 5.346q-.758 0-1.302-.544a1.78 1.78 0 0 1-.544-1.302q0-.758.544-1.302A1.78 1.78 0 0 1 17 1.654q.758 0 1.302.544.544.545.544 1.302 0 .758-.544 1.302A1.78 1.78 0 0 1 17 5.346"
                            ></path>
                        </svg>
                        <span className="font-semibold">Activities</span>
                    </button>
                    <button className="flex items-center gap-2 text-sas-text px-3 py-1 rounded-full hover:bg-gray-100 transition-colors">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            className="w-5 h-5"
                        >
                            <path
                                fill="currentColor"
                                d="M4.5 15.308V6q0-1.152.625-1.843T6.793 3.1t2.4-.483T12 2.5q1.554 0 2.92.118 1.367.12 2.387.483 1.02.364 1.607 1.056.585.69.586 1.843v9.308q0 1.34-.926 2.266t-2.266.926l.846.846q.329.33.157.741-.173.413-.638.413a.7.7 0 0 1-.26-.048.6.6 0 0 1-.22-.144L14.384 18.5h-4.77l-1.807 1.808a.6.6 0 0 1-.22.144.7.7 0 0 1-.261.048q-.45 0-.63-.413-.18-.412.15-.74l.845-.847q-1.34 0-2.266-.926T4.5 15.308M12 4q-2.794 0-4.077.351t-1.646.918h11.508q-.318-.597-1.608-.934Q14.887 4 12 4m-6 6.5h5.25V6.77H6zM16.308 12H6h12zm-3.558-1.5H18V6.77h-5.25zM8.5 15.808q.564 0 .935-.373.373-.372.373-.935t-.373-.935a1.27 1.27 0 0 0-.935-.373q-.563 0-.936.373-.372.372-.372.935t.372.935.936.373m7 0q.564 0 .936-.373.372-.372.372-.935t-.372-.935a1.27 1.27 0 0 0-.936-.373q-.563 0-.935.373-.373.372-.373.935t.373.935.935.373M7.692 17h8.616q.718 0 1.204-.488.488-.487.488-1.204V12H6v3.308q0 .718.487 1.204.488.488 1.205.488M12 5.27h5.785H6.277z"
                            ></path>
                        </svg>
                        <span className="font-semibold">Transfer</span>
                    </button>
                </div>
            </div>

            <DatePicker
                isOpen={showDatePicker}
                onClose={() => setShowDatePicker(false)}
                departureDate={departureDate}
                returnDate={returnDate}
                onDatesChange={handleDatesChange}
                tripType={tripType}
                onTripTypeChange={(newTripType) => {
                    setTripType(newTripType);

                    // Remove inboundDate when switching to One way
                    if (newTripType === "One way") {
                        setReturnDate("");
                        const newParams = new URLSearchParams(searchParams);
                        newParams.delete("inboundDate");
                        setSearchParams(newParams);
                    }
                }}
                payWithPoints={payWithPoints}
                onPayWithPointsChange={setPayWithPoints}
            />
        </div>
    );
};

export default BookingWidget;
